<template>
  <div
    class="trip-review"
    :class="{'no-scroll' : !priceCheckComplete, 'no-radius' : priceCheckComplete}"
  >
    <span v-if="!priceCheckComplete" class="loading-prompt">
      <b class="prompt-header">
        <span>Great choice!</span>
        <br />loading your itinerary details.
      </b>
      <fade-loader class="prompt-loader" :loading="!priceCheckComplete" color="#0fa5a4" size="40px"></fade-loader>
    </span>
    <b-tabs v-else v-model="activeTab">
      <b-tab-item label="Optional extras">
        <div class="air-rules">
          <div v-if="products">
            <b-message>
              <div class="block products-list">
                <div
                  class="margin-bot"
                  v-for="(product, index) in priceResponse.products"
                  :key="'product-' + index"
                >
                  <div class="width-100">
                    <b-checkbox v-model="selectedProducts" :native-value="product.id">
                      <b class="no-wrap">{{ product.name }}:</b>
                    </b-checkbox>
                    <b class="pushed-righ">R{{ product.amount }}</b>
                  </div>
                  <div class="product-description">{{ product.description }}</div>
                  <b-message
                    class="product-warning"
                    type="is-danger"
                    v-if="
                      !selectedProducts.includes(product.id) &&
                        product.warningText
                    "
                    v-html="product.warningText"
                  ></b-message>
                </div>
              </div>
            </b-message>
          </div>
          <div class="progress-buttons">
            <b-button class="margin-right my-button" type="is-warning" @click="resetFlights()">Back</b-button>
            <b-button
              :disabled="!priceCheckComplete"
              class="my-button"
              type="is-secondary"
              @click="confirmFlights()"
            >Continue</b-button>
          </div>
        </div>
      </b-tab-item>
      <b-tab-item label="Flight breakdown">
        <div class="flight-info">
          <h1 class="price">Price Breakdown for {{ priceResponse.travellerCount }} Passenger(s)</h1>
          <b-message type="is-primary" class="no-margin-bottom">
            <span class="price-item">
              Airfare per adult:
              <b>R{{ Math.ceil(priceResponse.fareBreakdown.adults.baseFare / priceResponse.travellers.adults) }}</b>
            </span>
            <span class="price-item" v-if="priceResponse.fareBreakdown.children">
              Airfare per child:
              <b>
                R{{
                Math.ceil(priceResponse.fareBreakdown.children.baseFare / priceResponse.travellers.children)
                }}
              </b>
            </span>
            <span class="price-item" v-if="priceResponse.fareBreakdown.infants">
              Airfare per infant:
              <b>
                R{{
                Math.ceil(priceResponse.fareBreakdown.infants.baseFare / priceResponse.travellers.infants)
                }}
              </b>
            </span>
            <span class="price-item">
              Total airport taxes:
              <b>R{{ Math.ceil(priceResponse.fareBreakdown.taxAmount) }}</b>
            </span>
            <span class="price-item" v-if="productsCost > 0">
              Total extras:
              <b>
                R{{
                Math.ceil(productsCost)
                }}
              </b>
            </span>
            <span class="price-item">
              Total price:
              <b>R{{ Math.ceil(priceResponse.totalAmount + productsCost) }}</b>
            </span>
          </b-message>
          <h1 class="flights">Flights Breakdown</h1>
          <b-message type="is-primary" v-for="flight in allFlights" :key="flight.index">
            <b>Flight:</b>
            {{ searchResults.airlineNames[flight.airlineCode] }}
            {{ flight.flightNumber }}
            <span
              class="dulled-text"
              v-if="flight.opAirlineCode"
            >- operated by {{ flight.opAirlineCode.toLowerCase() }}</span>
            <br />
            <b>Depart:</b>
            {{ formatDate(flight.departureDateTime) }} from ({{
            flight.origCode
            }}) {{ determineLocationFromCode(flight.origCode).airport }}
            <br />
            <b>Arrive:</b>
            {{ formatDate(flight.arrivalDateTime) }} at ({{ flight.destCode }})
            {{ determineLocationFromCode(flight.destCode).airport }},
            <br />
            <b>Cabin:</b>
            {{ flight.cabinClass.toLowerCase() }}
          </b-message>
          <b-message v-for="(leg, index) in selectedFlights.outbound.odoList" :key="'leg-' + index">
            <b
              class="flight"
            >Baggage {{leg.segments[0].origCode}} to {{leg.segments[leg.segments.length - 1].destCode}}:</b>
            <br />
            <span v-for="(bag, index) in leg.segments[0].baggage" :key="'bag-' + index">
              <span
                v-if="bag.type=='CHECKED' && !bag.name.includes('No') && !bag.name.includes('+')"
              >{{bag.name}} checked baggage</span>
              <span
                v-else-if="bag.name.includes('+')"
              >{{bag.name}} available at passenger details step</span>
              <span v-else-if="bag.name.includes('No')">No checked baggage</span>
              <span v-else>{{bag.name}}</span>
              <br />
            </span>
          </b-message>
          <div v-if="!isEmpty(selectedFlights.return)">
            <b-message v-for="(leg, index) in selectedFlights.return.odoList" :key="'leg-' + index">
              <b
                class="flight"
              >Baggage {{leg.segments[0].origCode}} to {{leg.segments[leg.segments.length - 1].destCode}}:</b>
              <br />
              <span v-for="(bag, index) in leg.segments[0].baggage" :key="'bag-' + index">
                <span
                  v-if="bag.type=='CHECKED' && !bag.name.includes('No') && !bag.name.includes('+')"
                >{{bag.name}} checked baggage</span>
                <span
                  v-else-if="bag.name.includes('+')"
                >{{bag.name}} available at passenger details step</span>
                <span v-else-if="bag.name.includes('No')">No checked baggage</span>
                <span v-else>{{bag.name}}</span>
                <br />
              </span>
            </b-message>
          </div>
        </div>
        <div class="progress-buttons">
          <b-button class="margin-right my-button" type="is-warning" @click="resetFlights()">Back</b-button>
          <b-button
            :disabled="!priceCheckComplete"
            class="my-button"
            type="is-primary"
            @click="confirmFlights()"
          >Continue</b-button>
        </div>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import Api from "@/services/Api.service.js";
import FadeLoader from "vue-spinner/src/FadeLoader.vue";

export default {
  name: "reviewModal",
  data: function() {
    return {
      fareBreakdownIsOpen: false,
      passengers: [],
      selectedProducts: [],
      activeTab: 0,
      priceCheckResult: undefined,
      priceCheckComplete: false
    };
  },
  components: {
    FadeLoader
  },
  watch: {},
  computed: {
    ...mapState([
      "selectedFlights",
      "flags",
      "searchResults",
      "priceResponse",
      "products"
    ]),
    productsCost() {
      let result = 0;
      this.selectedProducts.forEach(selected => {
        result += this.priceResponse.products.find(e => e.id == selected)
          .amount;
      });
      return result;
    },
    allFlights() {
      let flights = [];
      for (let itinerary of this.priceResponse.itineraries) {
        for (let leg of itinerary.odoList) {
          for (let segment of leg.segments) {
            segment.specialAttributes = itinerary.specialServiceAttributes;
            flights.push(segment);
          }
        }
      }
      this.$store.commit("setAllFlights", flights);
      return flights;
    }
  },
  methods: {
    isEmpty(obj) {
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) return false;
      }
      return true;
    },
    determineLocationFromCode(code) {
      if (this.searchResults) {
        return this.searchResults.airportInfos.find(e => {
          return e.iataCode == code;
        });
      }
      return undefined;
    },
    priceChange() {
      this.$buefy.notification.open({
        duration: 5000,
        message: `Price increase detected, please review carefully.`,
        position: "is-bottom-right",
        type: "is-danger",
        hasIcon: true
      });
    },
    async priceCheck() {
      const result = await Api.priceCheck({
        data: this.searchResults.data,
        sessionID: this.$store.state.session,
        outboundItineraryId: this.selectedFlights.outbound.id,
        inboundItineraryId: this.flags.isDomesticReturn
          ? this.selectedFlights.return.id
          : undefined
      });
      this.priceCheckResult = result.data;
      if (result.data.products) {
        result.data.products.forEach(product => {
          this.selectedProducts.push(product.id);
        });
      }
      if (result.data.priceIncrease) {
        this.priceChange();
      }
      this.$store.commit("setPriceResponse", result.data);
      this.priceCheckComplete = true;
    },
    formatDate(date) {
      return moment(date).format("h:mm a MMMM Do");
    },
    resetFlights() {
      this.priceCheckResult = {};
      this.priceCheckComplete = false;
      this.$store.commit("resetSelectedFlights");
      this.$store.commit("toggleFlightReview");
    },
    confirmFlights() {
      if (this.activeTab === 0) {
        this.activeTab = 1;
        return true;
      }
      this.$store.commit("updateProducts", this.selectedProducts);
      this.$store.commit("setPassengers", this.passengers);
      this.$store.commit("toggleFlightReview");
      this.$router.push("/details");
    }
  },
  mounted() {
    this.priceCheck();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/settings.scss";

.rule-format {
  display: flex;
  flex-direction: column;
}

.no-wrap {
  white-space: nowrap;
}

.width-100 {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.products-list {
  display: flex;
  flex-direction: column;
}

.product-description {
  font-size: 0.8em;
  margin-top: 0.5em;
}

.product-warning {
  padding: 0.75em;
  background-color: #ffe0e6 !important;
}

.margin-bot {
  margin-bottom: 1.2em;
}

.rule-heading {
  font-size: 1.1em;
  font-weight: bold;
  margin-right: 0.75em;
}

.heading-format {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.no-radius {
  boarder-radius: 0 !important;
}

.option-header {
  font-weight: bold;
  font-size: 1.1em;
}

.price-item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.trip-review {
  background: $light;
  margin: auto 1em;
  border-radius: 8px;
  min-height: 300px;
  box-shadow: 0 1px 6px rgba(32, 33, 36, 0.28);
  max-width: 600px !important;
  max-height: 80%;
  overflow-y: scroll;
}

.no-scroll {
  overflow-y: hidden;
}

.air-rules {
  img {
    max-width: 100px;
    margin-right: 0.75em;
  }
  display: flex;
  flex-direction: column;
}

.rules-loader-wrapper {
  width: 100%;
  height: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
}

.flight-info {
  display: flex;
  flex-direction: column;
}

.return-baggage {
  margin-top: 0.5em;
}

.message-body {
  padding: 1em;
}

.loading-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden !important;
  height: 100%;
}

.prompt-header {
  font-size: 1.4em;
  text-align: center;
  font-style: italic;
  color: grey;
  margin-top: 1rem;
  padding: 1rem;
  span {
    font-size: 1.2em;
    font-style: normal;
    font-weight: bold;
    color: $primary-color;
    line-height: 1.6em;
  }
}

.prompt-loader {
  margin-top: 3rem;
}

.dulled-text {
  font-size: 0.8em;
  font-style: italic;
}

.inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 1em;
}

.no-margin-bottom {
  margin-bottom: 0 !important;
}

.progress-buttons {
  display: flex;
  width: 100%;
  position: sticky;
  bottom: 0;
  left: 0;
  padding: 0.75em;
  background: white;
  justify-content: flex-end;
  align-items: center;
}

.margin-left-auto {
  margin-top: 0.35em;
}

.margin-right {
  margin-right: 0.5em;
  color: white;
}

@media (min-width: 781px) {
  // .inline {
  //     flex-direction: row;
  //     flex-wrap: nowrap;
  //     align-items: center;
  //     justify-content: flex-start;
  // }
}
</style>

<style lang="scss">
.message-header + .message-body {
  padding-top: 0 !important;
}

.no-radius {
  border-radius: 0 !important;
}

.message-header {
  p {
    color: white;
  }
}

.mango-and-radixx {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
}

.rule {
  padding: 0 0 1em 0;
}

.modal .animation-content {
  display: flex;
  max-height: 90%;
}

.my-button {
  font-size: 0.8em !important;
}

@media (min-width: 781px) {
  .my-button {
    font-size: 1rem !important;
  }
}
</style>
